"use client";

import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HeroUIProvider } from "@heroui/system";
import { useRouter } from "next/navigation";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { PrivyProvider } from "@privy-io/react-auth";
import PlausibleProvider from "next-plausible";

import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/contexts/auth-context";
import { TurnstileProvider } from "@/contexts/turnstile-context";
import { ModalProvider } from "@/contexts/modal-context";
import { ReferralConsumer } from "@/components/ReferralConsumer";
import { Toaster } from "sonner";

export interface ProvidersProps {
  children: React.ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const router = useRouter();

  return (
    <PlausibleProvider
      customDomain="https://analytics.pulsar.money"
      domain="portal.swapper.finance"
    >
      <PrivyProvider
        appId={process.env.NEXT_PUBLIC_PRIVY_APP_ID || ""}
        config={{
          loginMethods: ["wallet"],
          appearance: {
            theme: "light",
            accentColor: "#EA5555",
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TurnstileProvider>
            <ModalProvider>
              <HeroUIProvider navigate={(path) => router.push(path)}>
                <NextThemesProvider attribute="class" defaultTheme="light">
                  <ReferralConsumer />
                  <Toaster />
                  {children}
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-right"
                  />
                </NextThemesProvider>
              </HeroUIProvider>
            </ModalProvider>
            </TurnstileProvider>
          </AuthProvider>
        </QueryClientProvider>
      </PrivyProvider>
    </PlausibleProvider>
  );
}
